import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useAdminApi } from '@/hooks/useAdminApi';
import { Calendar, Loader2, Shield, UserMinus, UserPlus } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';

interface HQAdmin {
  id: string;
  email: string;
  name: string;
  is_active: boolean;
  created_at: string;
}

export default function AdminTeam() {
  const adminApi = useAdminApi();
  const adminApiRef = useRef(adminApi);
  adminApiRef.current = adminApi;

  // Data state
  const [admins, setAdmins] = useState<HQAdmin[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch HQ admins on mount - use ref to avoid dependency issues
  const fetchAdmins = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await adminApiRef.current.getUsers({ role: 'hq_admin', limit: 100 });

      if (response.success && response.data) {
        setAdmins(
          response.data.users.map((u) => ({
            id: u.id,
            email: u.email,
            name: u.name,
            is_active: u.is_active,
            created_at: u.created_at,
          }))
        );
      }
    } catch (err) {
      console.error('Error fetching HQ admins:', err);
      setError(err instanceof Error ? err.message : 'Failed to load HQ team');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAdmins();
  }, [fetchAdmins]);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const activeCount = admins.filter((a) => a.is_active).length;
  const inactiveCount = admins.filter((a) => !a.is_active).length;

  return (
    <DashboardLayout>
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gradient-gold">HQ Team</h1>
        <p className="mt-1 text-muted-foreground">
          ErgShoot headquarters administrators. Use{' '}
          <a href="/admin/users" className="text-primary hover:underline">
            Users &amp; Roles
          </a>{' '}
          to invite new admins.
        </p>
      </div>

      {/* Stats */}
      <div className="grid gap-4 md:grid-cols-3 mb-8">
        <Card className="animate-fade-in">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="rounded-lg bg-purple-500/10 p-3">
                <Shield className="h-6 w-6 text-purple-500" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Admins</p>
                <p className="text-2xl font-bold">{admins.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="animate-fade-in" style={{ animationDelay: '50ms' }}>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="rounded-lg bg-green-500/10 p-3">
                <UserPlus className="h-6 w-6 text-green-500" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Active</p>
                <p className="text-2xl font-bold">{activeCount}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="animate-fade-in" style={{ animationDelay: '100ms' }}>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="rounded-lg bg-red-500/10 p-3">
                <UserMinus className="h-6 w-6 text-red-500" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Inactive</p>
                <p className="text-2xl font-bold">{inactiveCount}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Team List */}
      <Card className="animate-fade-in" style={{ animationDelay: '150ms' }}>
        <CardHeader>
          <CardTitle>Team Members</CardTitle>
          <CardDescription>All HQ administrators with platform access</CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : error ? (
            <div className="text-center py-12">
              <p className="text-destructive">{error}</p>
              <Button variant="outline" className="mt-4" onClick={fetchAdmins}>
                Try Again
              </Button>
            </div>
          ) : admins.length === 0 ? (
            <div className="text-center py-12">
              <Shield className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">No HQ admins found</p>
              <p className="text-sm text-muted-foreground mt-2">
                Use{' '}
                <a href="/admin/users" className="text-primary hover:underline">
                  Users &amp; Roles
                </a>{' '}
                to invite admins.
              </p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Joined</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {admins.map((admin) => (
                  <TableRow key={admin.id}>
                    <TableCell className="font-medium">
                      <div className="flex items-center gap-3">
                        <div className="flex h-8 w-8 items-center justify-center rounded-full bg-purple-500/10 text-purple-500">
                          {admin.name.charAt(0).toUpperCase()}
                        </div>
                        {admin.name}
                      </div>
                    </TableCell>
                    <TableCell className="text-muted-foreground">{admin.email}</TableCell>
                    <TableCell>
                      <Badge
                        variant="outline"
                        className={
                          admin.is_active
                            ? 'bg-green-500/10 text-green-500 border-green-500/20'
                            : 'bg-red-500/10 text-red-500 border-red-500/20'
                        }
                      >
                        {admin.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      <div className="flex items-center gap-2">
                        <Calendar className="h-4 w-4" />
                        {formatDate(admin.created_at)}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </DashboardLayout>
  );
}
